import React, {FC} from 'react';

import {Trans} from '../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {addThemedScrollbarStyles} from '../../../libs/shared/ui/src/styles/mixins';
import {
  LightUserThemeProvider,
  styled,
  useUserTheme,
} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {CalendarsPanelId} from '../../store/panels/panelStateBase';
import {Grid} from '../common/grid';
import {DynamicScrollBoundaries} from '../common/scroll/dynamicScrollBoundaries';
import {RenderLargeCalendarsLeftPanel} from './largeCalendarsBase';
import {LargeCalendarsContainer} from './largeCalendarsContainer';

/*
 * Props.
 */

interface PopupLargeCalendarsProps {
  panelId: CalendarsPanelId;
  canCreateNewEvent: boolean;
  onNewEventClick: Handler;
  leftPanelWidth: number;
  onLeftPanelWidthChange: HandlerOf<number>;
}

/*
 * Style.
 */

const StyledLeftPanelDiv = styled(Grid)`
  display: grid;
  grid-template-rows: auto 1fr;
  background-color: ${(p) => p.theme.background};
`;

const StyledNewEventDiv = styled.div`
  padding: 16px;
  display: flex;
`;

const StyledNewEventContentDiv = styled.div`
  padding-right: 6px;

  display: grid;
  grid-template-columns: auto auto;
  grid-column-gap: 8px;
  align-items: center;
`;

const StyledContentDiv = styled.div`
  position: relative;
  overflow: auto;
  padding-bottom: 16px;
  ${addThemedScrollbarStyles()};
`;

/*
 * Component.
 */

export const PopupLargeCalendars: FC<PopupLargeCalendarsProps> = (props) => {
  const {panelId, canCreateNewEvent, onNewEventClick, leftPanelWidth, onLeftPanelWidthChange} = props;
  const {itemSeparator} = useUserTheme();

  // Use a custom component to render the date picker and calendars list.
  const renderLeftPanel: RenderLargeCalendarsLeftPanel = (width, content) => (
    <StyledLeftPanelDiv style={{width}}>
      <StyledNewEventDiv>
        <Button
          variant={ButtonVariantsEnum.PRIMARY}
          size={VisualSizesEnum.MEDIUM}
          isDisabled={!canCreateNewEvent}
          onClick={onNewEventClick}
        >
          <StyledNewEventContentDiv>
            <Icon name="newTiny" />
            <Trans id="calendars-sidebar-newevent">New event</Trans>
          </StyledNewEventContentDiv>
        </Button>
      </StyledNewEventDiv>
      <DynamicScrollBoundaries hasTopBoundary color={itemSeparator}>
        <StyledContentDiv>{content}</StyledContentDiv>
      </DynamicScrollBoundaries>
    </StyledLeftPanelDiv>
  );

  return (
    // TODO: Remove theme provider when we fully support dark mode in calendar.
    <LightUserThemeProvider>
      <LargeCalendarsContainer
        panelId={panelId}
        isVisible
        renderLeftPanel={renderLeftPanel}
        leftPanelWidth={leftPanelWidth}
        onLeftPanelWidthChange={onLeftPanelWidthChange}
      />
    </LightUserThemeProvider>
  );
};
